import React from "react";
import { Chip, Tooltip, Typography, Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField } from "@material-ui/core";

// Shared workflow widgets for all 11 work types.
//   deriveStatus(row)          → "pending" | "approved" | "revisionRequested" | "resubmitted"
//   <WorkflowStatusChip ...>   → renders the 4-state chip + reason tooltip
//   <RevisionRequestButton .>  → renders "Yêu cầu khai lại" link + modal flow
// Each table only wires (a) the action button in its action column,
// (b) the chip in its status column, (c) the dialog at the bottom.

export const deriveStatus = (row) => {
  if (row?.revisionStatus === "requested") return "revisionRequested";
  if (row?.revisionStatus === "resubmitted") return "resubmitted";
  if (row?.accepted) return "approved";
  return "pending";
};

export const STATUS_META = {
  pending: { label: "Chờ duyệt", bg: "#9e9e9e", color: "#fff" },
  approved: { label: "Đã duyệt", bg: "#43a047", color: "#fff" },
  revisionRequested: { label: "Yêu cầu khai lại", bg: "#e53935", color: "#fff" },
  resubmitted: { label: "Đã khai lại — chờ duyệt", bg: "#1e88e5", color: "#fff" },
};

export const fmtDateTime = (v) => {
  if (!v) return "—";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("vi-VN", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

// 3 read-only columns common to every work-type table:
//   "Người kê khai" / "Người duyệt" / "Thời gian duyệt"
// Each table only does `columns.push(...workflowMetaColumns())` to get them.
// Pass { width: "8%" } etc to override defaults.
export const workflowMetaColumns = (opts = {}) => [
  {
    title: <b>Người kê khai</b>,
    key: "createdByName",
    width: opts.width || "10%",
    searchable: true,
    render: (value) => (value ? value : <span style={{ color: "#bbb" }}>—</span>),
  },
  {
    title: <b>Người duyệt</b>,
    key: "approvedByName",
    width: opts.width || "10%",
    render: (value) => (value ? value : <span style={{ color: "#bbb" }}>—</span>),
  },
  {
    title: <b>Thời gian duyệt</b>,
    key: "approvedAt",
    width: opts.width || "10%",
    sortable: true,
    sorter: (a, b) => new Date(a.approvedAt || 0) - new Date(b.approvedAt || 0),
    render: (value) => (value ? fmtDateTime(value) : <span style={{ color: "#bbb" }}>—</span>),
  },
];

export function WorkflowStatusChip({ row }) {
  if (row?.purposeId === 4) {
    return <Chip size="small" color="primary" label="Công bố cũ" variant="outlined" />;
  }
  const status = deriveStatus(row);
  const meta = STATUS_META[status] || STATUS_META.pending;
  return (
    <Tooltip
      title={row.revisionStatus === "requested" && row.revisionReason ? `Lý do: ${row.revisionReason}` : ""}
      placement="top"
    >
      <Chip size="small" label={meta.label} style={{ backgroundColor: meta.bg, color: meta.color, fontWeight: 500 }} />
    </Tooltip>
  );
}

// Trigger button — admin only. Hides itself when row is already in
// revision_status='requested' (no point asking twice).
export function RevisionRequestLink({ row, onClick }) {
  if (row.revisionStatus === "requested") return null;
  return (
    <Typography
      className="table-action-buton"
      variant="caption"
      style={{ color: "#e53935", cursor: "pointer" }}
      onClick={(e) => {
        e.stopPropagation?.();
        onClick(row);
      }}
    >
      Yêu cầu khai lại
    </Typography>
  );
}

// Drop into a table's render tree — controlled by a single state variable.
// Reason must be >= 10 chars; the BE enforces this too.
export function RevisionRequestDialog({ row, onClose, onSubmit, submitting }) {
  const [reason, setReason] = React.useState("");
  React.useEffect(() => {
    setReason(row?.revisionReason || "");
  }, [row?.id]);

  return (
    <Dialog
      open={!!row}
      onClose={() => {
        if (!submitting) onClose();
      }}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>Yêu cầu khai lại</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" style={{ marginBottom: 12, color: "#555" }}>
          Công trình: <b>{row?.title || row?.code || `#${row?.id}`}</b>
        </Typography>
        <Typography variant="caption" style={{ display: "block", color: "#666", marginBottom: 4 }}>
          Lý do (tối thiểu 10 ký tự). Người kê khai sẽ nhận lý do này qua email + thấy trên bảng công trình.
        </Typography>
        <TextField
          autoFocus
          fullWidth
          multiline
          minRows={4}
          variant="outlined"
          placeholder="Ví dụ: Vui lòng đính kèm file PDF bản chính thức."
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          disabled={submitting}
        />
      </DialogContent>
      <DialogActions>
        <Button disabled={submitting} onClick={onClose}>
          Huỷ
        </Button>
        <Button
          color="primary"
          variant="contained"
          disabled={submitting || (reason || "").trim().length < 10}
          onClick={() => onSubmit(reason.trim())}
        >
          {submitting ? "Đang gửi..." : "Gửi yêu cầu"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
